import { isShopperSubtype } from '../constants';
import { Message } from '../types';

/**
 * The subset of message fields the Shopper guards read. Both the sales
 * `Message` and the raw `ChatHistory` item satisfy this shape, so the guards
 * are shared by the message list and the conversation filters.
 */
export type ShopperMessageFields = Pick<
  Message,
  'type' | 'subtype' | 'direction' | 'source'
>;

const SHOPPER_AUTOMATION_SUBTYPE_PATTERN =
  /^(?:shopper-automations-)?([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})$/;

export const getShopperAutomationMessageGuid = (
  message: ShopperMessageFields,
): string | undefined =>
  message.type === 'sender' || message.type === 'flow_action'
    ? message.subtype?.match(SHOPPER_AUTOMATION_SUBTYPE_PATTERN)?.[1]
    : undefined;

export const MESSAGE_SOURCE = {
  INFINITY_TESTING: 'infinity_testing',
  SHOPPER_AUTOMATIONS: 'shopper_automations',
} as const;

/** Returns true for messages originating from an Infinity Test flow action. */
export const isITMessage = (message: Message): boolean =>
  message.source === MESSAGE_SOURCE.INFINITY_TESTING;

export const isShopperAutomationSource = (
  message: ShopperMessageFields,
): boolean => message.source === MESSAGE_SOURCE.SHOPPER_AUTOMATIONS;

/**
 * Returns true for messages originating from Shopper Automations.
 *
 * `sender` and `flow_action` are the API's Shopper Automations type set --
 * see SHOPPER_AUTOMATION_TYPES in billing/core/queries.py and
 * SHOPPER_AUTOMATIONS_TYPES in
 * messaging/conversations/sent_message_details/services.py, which both bill
 * those two types as Shopper Automations sends.
 */
export const isShopperAutomationMessage = (
  message: ShopperMessageFields,
): boolean =>
  isShopperAutomationSource(message) ||
  message.type === 'sender' ||
  getShopperAutomationMessageGuid(message) != null;

/** True for outgoing Shopper Responses (conversation-thread AI replies). */
export const isShopperMessage = (message: ShopperMessageFields): boolean =>
  message.direction === 'outgoing' && isShopperSubtype(message.subtype);

// The on-thread opt-in subtypes Shopper sends. These are Shopper replies but
// do not carry an `ssai` subtype, so isShopperMessage alone misses them.
// Source of truth: ShopperReplySubType in the API's self_serve_ai/constants.py
// (CONSENT_PROMPT, CONSENT_CONFIRMATION, CONSENT_DECLINE_ACK).
const SHOPPER_CONSENT_SUBTYPES: readonly string[] = [
  'ai-consent-presentation',
  'ai-consent-confirmation',
  'ai-consent-decline',
];

/** True for the on-thread opt-in prompt and its confirmation/decline acks. */
export const isShopperConsentMessage = (
  message: ShopperMessageFields,
): boolean =>
  message.direction === 'outgoing' &&
  message.subtype != null &&
  SHOPPER_CONSENT_SUBTYPES.includes(message.subtype);

const SHOPPER_CAMPAIGN_SUBTYPES: readonly string[] = [
  'shopper-campaigns',
  'shopper-campaigns-control',
];

/** True for messages sent by a Shopper Campaign send flow. */
export const isShopperCampaignMessage = (
  message: ShopperMessageFields,
): boolean =>
  message.type === 'flow_action' &&
  message.subtype != null &&
  SHOPPER_CAMPAIGN_SUBTYPES.includes(message.subtype);
